import { motion } from "framer-motion";
import { ExternalLink, Megaphone, Wallet, Eye, Users, FileText, Activity } from "lucide-react";
import { fmtFull } from "@/lib/format";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0 })}`;

function Cell({ icon: Icon, label, value, valueClassName }) {
  return (
    <div className="rounded-xl border border-black/[0.06] bg-neutral-50 px-4 py-3">
      <div className="flex items-center gap-1.5 text-neutral-500">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className={`num-display mt-1 text-lg font-extrabold text-[#111] ${valueClassName || ""}`}>{value}</p>
    </div>
  );
}

const signedMoney = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0 })}`;

// Source-linked Promote.fun snapshot. Real numbers (including real zeros) — NOT a synced feed
// and NOT a fabricated creator post. Clearly labelled as a point-in-time snapshot.
export function PromoteSourceCard({ promote }) {
  if (!promote) return null;
  const p = promote;
  const overBudget = typeof p.remaining === "number" && p.remaining < 0;
  const hasReach = p.reach !== null && p.reach !== undefined;
  return (
    <motion.div
      data-testid="promote-source-card"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-card mt-6 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#35606E] text-white"><Megaphone className="h-4 w-4" /></span>
            <div>
              <p className="font-display text-base font-extrabold leading-tight">{p.campaign_name || "Promote campaign"}</p>
              <p className="text-xs text-neutral-500">
                promote.fun{p.creator ? <> · creator @{p.creator}</> : null} · <span className="font-semibold text-[#065F46]">{p.status}</span>
              </p>
            </div>
          </div>
        </div>
        {p.source_url && (
          <a
            data-testid="promote-source-link"
            href={p.source_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition-colors hover:bg-black hover:text-white"
          >
            View source <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Cell icon={Wallet} label="Budget" value={money(p.budget)} />
        <Cell icon={Wallet} label="Used" value={money(p.used)} />
        <Cell
          icon={Wallet}
          label="Remaining"
          value={signedMoney(p.remaining)}
          valueClassName={overBudget ? "!text-[#B91C1C]" : ""}
        />
        <Cell icon={Wallet} label="CPM" value={money(p.cpm)} />
        <Cell icon={Eye} label="Views" value={fmtFull(p.views)} />
        <Cell icon={Activity} label="Reach" value={hasReach ? fmtFull(p.reach) : "—"} />
        <Cell icon={FileText} label="Submissions" value={fmtFull(p.submissions)} />
        <Cell icon={Users} label="Unique Creators" value={fmtFull(p.unique_creators)} />
      </div>

      {overBudget && (
        <p data-testid="promote-over-budget" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FEF2F2] px-3 py-1 text-[11px] font-bold text-[#B91C1C] ring-1 ring-[#B91C1C]/20">
          Over budget by {signedMoney(Math.abs(p.remaining))}
        </p>
      )}

      {Array.isArray(p.unique_creator_accounts) && p.unique_creator_accounts.length > 0 && (
        <p className="mt-3 text-[11px] text-neutral-500">
          Creators: <span className="font-medium text-neutral-700">{p.unique_creator_accounts.join(", ")}</span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-400">
        <span>Engagement rate {Number(p.engagement_rate || 0).toFixed(2)}%</span>
        <span>· {fmtFull(p.pages)} pages</span>
        {p.resolves_to && <span>· Audio: {p.resolves_to} (music {p.tiktok_music_id})</span>}
      </div>
      <p className="mt-2 text-[11px] font-medium text-[#92400E]">
        {p.snapshot_label
          ? p.snapshot_label
          : `Snapshot as of ${(p.snapshot_as_of || "").replace("T", " ").slice(0, 16)} ${p.snapshot_tz} — point-in-time, not live-synced.${p.launch_at ? ` Launch ${p.launch_at.replace("T", " ")} (${p.launch_tz_note}).` : ""}`}
      </p>
    </motion.div>
  );
}
